import { ARKON } from './systems.consts';
import { Procedure } from '@medplum/fhirtypes';

// =============================================================================
// FHIR Constants
// =============================================================================

/**
 * Status options for therapeutic service procedures.
 * Used in: TherapeuticServiceModal.tsx
 */
export const STATUS_OPTIONS: Procedure['status'][] = [
  'in-progress',
  'stopped',
  'completed',
  'entered-in-error',
];

/**
 * Default status for therapeutic service procedures.
 * New therapeutic services default to 'in-progress'.
 * Used in: procedure.utils.ts
 */
export const DEFAULT_STATUS = 'in-progress' as const;

/**
 * ARKON ValueSet binding for therapeutic interventions (Greek)
 * Used in: TherapeuticServiceModal.tsx
 */
export const THERAPEUTIC_SERVICE_VALUESET =
  ARKON.VS.THERAPEUTIC_INTERVENTIONS_EL;

/**
 * ARKON CodeSystem for therapeutic interventions
 * Used in: procedure.utils.ts (isTherapeuticService check)
 */
export const THERAPEUTIC_SERVICE_SYSTEM =
  ARKON.CS.THERAPEUTIC_INTERVENTION.BASE;

// =============================================================================
// Translation Keys
// =============================================================================

/**
 * Type-safe translation keys for therapeutic services.
 * Ensures compile-time checking of translation key usage.
 *
 * Note: This type is currently not enforced in the codebase.
 * To enforce type safety, update useTranslation calls to use this type:
 * const { t } = useTranslation<TherapeuticServiceTranslationKey>(['patient-pages']);
 */
export type TherapeuticServiceTranslationKey =
  // Section headers
  | 'therapeuticServices.title'
  | 'therapeuticServices.addButton'
  | 'therapeuticServices.empty'
  | 'therapeuticServices.unknownService'

  // Field labels
  | 'therapeuticServices.serviceLabel'
  | 'therapeuticServices.statusLabel'
  | 'therapeuticServices.statusOptions.in-progress'
  | 'therapeuticServices.statusOptions.stopped'
  | 'therapeuticServices.statusOptions.completed'
  | 'therapeuticServices.statusOptions.entered-in-error'
  | 'therapeuticServices.startDateLabel'
  | 'therapeuticServices.terminationDateLabel'
  | 'therapeuticServices.onsetDate'
  | 'therapeuticServices.terminationDate'
  | 'therapeuticServices.ageAtStart'
  | 'therapeuticServices.startedAtAge'
  | 'therapeuticServices.responseLabel'
  | 'therapeuticServices.responsePlaceholder'

  // Actions
  | 'therapeuticServices.actions.edit'
  | 'therapeuticServices.actions.stop'
  | 'therapeuticServices.actions.delete'

  // Sections
  | 'therapeuticServices.sections.active'
  | 'therapeuticServices.sections.stoppedThisEncounter'
  | 'therapeuticServices.sections.previouslyStopped'

  // Modal
  | 'therapeuticServices.modal.addTitle'
  | 'therapeuticServices.modal.editTitle'
  | 'therapeuticServices.modal.addButton'
  | 'therapeuticServices.modal.saveButton'
  | 'therapeuticServices.modal.validationServiceRequired'
  | 'therapeuticServices.modal.validationDateRequired'
  | 'therapeuticServices.modal.validationEndDateRequired'
  | 'therapeuticServices.modal.validationDateRange'
  | 'therapeuticServices.modal.saveErrorTitle'

  // Notifications
  | 'therapeuticServices.notifications.saveSuccess'
  | 'therapeuticServices.notifications.saveSuccessMessage'
  | 'therapeuticServices.notifications.saveError'
  | 'therapeuticServices.notifications.deleteSuccess'
  | 'therapeuticServices.notifications.deleteSuccessMessage'
  | 'therapeuticServices.notifications.deleteError'

  // Confirm Delete Dialog
  | 'therapeuticServices.confirmDelete.title'
  | 'therapeuticServices.confirmDelete.message'
  | 'therapeuticServices.confirmDelete.confirm'
  | 'therapeuticServices.confirmDelete.cancel'

  // Stop Modal
  | 'therapeuticServices.stopModal.title'
  | 'therapeuticServices.stopModal.description'
  | 'therapeuticServices.stopModal.endDateLabel'
  | 'therapeuticServices.stopModal.reasonLabel'
  | 'therapeuticServices.stopModal.reasonPlaceholder'
  | 'therapeuticServices.stopModal.customReasonLabel'
  | 'therapeuticServices.stopModal.customReasonPlaceholder'
  | 'therapeuticServices.stopModal.confirmButton'
  | 'therapeuticServices.stopModal.saveErrorTitle';
